import React, {ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {FaCompress, FaExpand, FaPause, FaPlay, FaVolumeDown, FaVolumeMute, FaVolumeUp} from 'react-icons/fa';
import './liveStreamPlayer.css';
import {TORRESOWO_URL} from "../../../../utils/consts";
import {cn, isOnlyMobileDevice, isSmallMediumScreen} from "../../../../utils/utils";
import useUserStorage from "../../../../hooks/useUserStorage";
import {Spinner} from "../../../shared";

type Props = {
    title: string;
    className?: string;
    autoPlay: boolean;
    src: string;
    otherStreams?: React.ReactNode;
    onClose?: () => void;
    extraBtns?: {
        label: ReactNode;
        title: string;
        onClick: (e) => void;
    }[];
}

function getSupportedCodec() {
    const codecs = [
        'video/mp4; codecs="avc1.42E01E"', // H.264 Baseline
        'video/mp4; codecs="avc1.4d401f"', // H.264 Main
        'video/mp4; codecs="avc1.640028"', // H.264 High
    ];

    return codecs.find(codec => MediaSource.isTypeSupported(codec)) || null;
}

const LiveStreamPlayer = (props: Props) => {
    const {
        title,
        className,
        autoPlay,
        src,
        onClose
    } = props;
    const videoRef = useRef<HTMLVideoElement>(null);
    const playerRef = useRef<HTMLDivElement>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [isPlaying, setIsPlaying] = useState(autoPlay);
    const [isMuted, setIsMuted] = useState(false);
    const [volume, setVolume] = useState(1);
    const [status, setStatus] = useState<'connecting' | 'buffering' | 'playing' | 'stalled' | 'retrying' | 'error'>('connecting');
    const [controlsVisible, setControlsVisible] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [touchStartTime, setTouchStartTime] = useState(0);
    const [errorMsg, setErrorMsg] = useState("");
    const controlsTimeout = 3000;
    const [firstLoad, setFirstLoad] = useState(false);
    const firstLoadRef = useRef(false);
    const showOtherStreamsStorage = useUserStorage({key: 'show-other-streams', defaultValue: true});

    useLayoutEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        if (!window.MediaSource) {
            console.error("MediaSource API not supported in this browser.");
            return;
        }

        setFirstLoad(false);
        firstLoadRef.current = false;
        const mediaSource = new MediaSource();
        let sourceBuffer: SourceBuffer | null = null;
        let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
        let streamClosed = false;
        video.src = URL.createObjectURL(mediaSource);

        mediaSource.addEventListener('sourceopen', async () => {
            const mimeCodec = 'video/mp4; codecs="avc1.42E01E, mp4a.40.2"';
            if (!MediaSource.isTypeSupported(mimeCodec)) {
                return;
            }
            sourceBuffer = mediaSource.addSourceBuffer(mimeCodec);
            sourceBuffer.mode = 'segments';
            try {
                const response = await fetch(src);
                if (!response.ok) throw new Error(`Stream error: ${response.status}`);
                reader = response.body.getReader();
                const appendChunk = async () => {
                    const {done, value} = await reader.read();
                    if (done) {
                        streamClosed = true;
                        if (mediaSource.readyState === 'open') {
                            mediaSource.endOfStream();
                        }
                        return;
                    }
                    if (sourceBuffer.updating) {
                        await new Promise(resolve => {
                            sourceBuffer.addEventListener('updateend', resolve, {once: true});
                        });
                    }
                    try {
                        sourceBuffer.appendBuffer(value);
                        if (sourceBuffer.buffered.length > 0 && !firstLoadRef.current) {
                            firstLoadRef.current = true;
                            setFirstLoad(true);
                            playerRef.current.requestFullscreen?.().catch(() => {
                            });
                        }
                    } catch (e) {
                        console.error("AppendBuffer error:", e);
                    }
                    appendChunk().catch(err => {
                        console.error("Error reading stream:", err);
                    });
                };
                await appendChunk();
            } catch (e) {
                console.error(e);
            }
        });

        return () => {
            // Limpiar el SourceBuffer
            if (sourceBuffer) {
                try {
                    if (mediaSource.readyState === 'open' && !streamClosed) {
                        mediaSource.endOfStream();
                    }
                    if (mediaSource.sourceBuffers.length > 0) {
                        mediaSource.removeSourceBuffer(sourceBuffer);
                    }
                } catch (e) {
                    console.error('Error al limpiar SourceBuffer:', e);
                }
            }
            // Cancelar el reader si existe
            if (reader) {
                try {
                    reader.cancel();
                } catch (e) {
                    console.error('Error al cancelar reader:', e);
                }
            }
            // Revocar el objeto URL
            if (video && video.src) {
                URL.revokeObjectURL(video.src);
            }
        };
    }, [src]);


    // Detectar si es móvil
    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth <= 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Ocultación automática de controles
    useEffect(() => {
        const player = playerRef.current;
        if (!player) return;
        const showControls = () => {
            setControlsVisible(true);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(() => {
                if (isPlaying) setControlsVisible(false);
            }, isMobile ? controlsTimeout * 1.5 : controlsTimeout);
        };
        const handleMouseMove = () => showControls();
        const handleMouseLeave = () => {
            if (isPlaying) setControlsVisible(false);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
        const handleMouseEnter = () => showControls();
        const handleTouchStart = () => {
            //setTouchStartTime(Date.now());
            showControls();
        };
        const handleTouchEnd = () => {
            //const touchDuration = Date.now() - touchStartTime;
            //if (touchDuration < 300) togglePlay();
        };
        player.addEventListener('mousemove', handleMouseMove);
        player.addEventListener('mouseleave', handleMouseLeave);
        player.addEventListener('mouseenter', handleMouseEnter);
        player.addEventListener('touchstart', handleTouchStart, {passive: true});
        player.addEventListener('touchend', handleTouchEnd);
        return () => {
            player.removeEventListener('mousemove', handleMouseMove);
            player.removeEventListener('mouseleave', handleMouseLeave);
            player.removeEventListener('mouseenter', handleMouseEnter);
            player.removeEventListener('touchstart', handleTouchStart);
            player.removeEventListener('touchend', handleTouchEnd);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, [controlsTimeout, isPlaying, touchStartTime, isMobile]);

    // Efecto para mostrar controles cuando se pausa
    useEffect(() => {
        if (!isPlaying) {
            setControlsVisible(true);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        } else {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(() => {
                setControlsVisible(false);
            }, controlsTimeout);
        }
    }, [isPlaying, controlsTimeout, controlsVisible]);

    // Lógica de reproducción y buffer
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;
        video.src = src;
        setFirstLoad(false);
        const onLoadStart = () => setStatus('connecting');
        const onWaiting = () => setStatus('buffering');
        const onPlaying = () => setStatus('playing');
        const onStalled = () => setStatus('stalled');
        const onCanPlay = () => setStatus('playing');
        const onError = () => {
            setStatus('error');
            setErrorMsg("No se pudo reproducir el stream.");
        };
        const loadedMetadataHandler = () => {
            setFirstLoad(true);
            if (!isFullscreen) {
                toggleFullscreen();
            }
        };
        video.addEventListener("loadstart", onLoadStart);
        video.addEventListener("waiting", onWaiting);
        video.addEventListener("playing", onPlaying);
        video.addEventListener("stalled", onStalled);
        video.addEventListener("canplay", onCanPlay);
        video.addEventListener("error", onError);
        video.addEventListener('loadedmetadata', loadedMetadataHandler);
        if (autoPlay) video.play().catch((e) => {
            console.error("Autoplay error:", e);
        });
        return () => {
            video.removeEventListener("loadstart", onLoadStart);
            video.removeEventListener("waiting", onWaiting);
            video.removeEventListener("playing", onPlaying);
            video.removeEventListener("stalled", onStalled);
            video.removeEventListener("canplay", onCanPlay);
            video.removeEventListener("error", onError);
            video.removeEventListener('loadedmetadata', loadedMetadataHandler);
        };
    }, [autoPlay, src]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;
        video.volume = volume;
        video.muted = isMuted;
    }, [volume, isMuted]);

    // Sincronizar flags desde eventos nativos
    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        const onPlay = () => setIsPlaying(true);
        const onPause = () => setIsPlaying(false);
        const onMuteChange = () => setIsMuted(v.muted);
        v.addEventListener("play", onPlay);
        v.addEventListener("pause", onPause);
        v.addEventListener("volumechange", onMuteChange);
        return () => {
            v.removeEventListener("play", onPlay);
            v.removeEventListener("pause", onPause);
            v.removeEventListener("volumechange", onMuteChange);
        };
    }, []);

    // Controles
    const togglePlay = () => {
        const v = videoRef.current;
        if (!v) return;
        if (v.paused) {
            v.play().then(() => setIsPlaying(true)).catch(() => {
            });
        } else {
            v.pause();
            setIsPlaying(false);
        }
    };
    const toggleMute = () => {
        const v = videoRef.current;
        if (!v) return;
        v.muted = !v.muted;
        setIsMuted(v.muted);
    };

    const toggleOtherStreams = () => {
        showOtherStreamsStorage.setItem(!showOtherStreamsStorage.item);
    }

    const onVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const v = videoRef.current;
        if (!v) return;
        const vol = Number(e.target.value);
        v.volume = vol;
        setVolume(vol);
        if (v.muted && vol > 0) {
            v.muted = false;
            setIsMuted(false);
        }
    };
    const toggleFullscreen = () => {
        const player = playerRef.current;
        if (!player) return;
        if (!document.fullscreenElement) {
            player.requestFullscreen?.().catch(() => {
            });
            if (isOnlyMobileDevice()) {
                if (window.screen.orientation?.lock) {
                    window.screen.orientation.lock("landscape").catch(() => {
                    });
                }
            }
        } else {
            document.exitFullscreen?.();
            if (isOnlyMobileDevice()) {
                if (window.screen.orientation?.lock) {
                    window.screen.orientation.lock("portrait").catch(() => {
                    });
                }
            }
        }
    };
    useEffect(() => {
        const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);
    const statusLabel = useMemo(() => {
        const mapping: Record<string, string> = {
            connecting: "Conectando…",
            buffering: "Cargando…",
            playing: "En vivo",
            stalled: "Reconectando…",
            retrying: "Reintentando…",
            error: "Error",
        };
        return mapping[status] || "";
    }, [status]);
    const getVolumeIcon = () => {
        if (isMuted || volume === 0) return <FaVolumeMute/>;
        else if (volume < 0.5) return <FaVolumeDown/>;
        return <FaVolumeUp/>;
    };

    return (
        <div
            className={`live-stream-player ${className || ""} ${!controlsVisible ? 'is-cursor-hidden' : ''} ${isMobile ? 'is-mobile' : ''}`}
            ref={playerRef}
        >
            <div className="live-stream-player__stage">
                {!firstLoad &&
                    <video
                        ref={(el) => {
                            if (el && videoRef.current) {
                                el.volume = (videoRef.current?.volume ?? 0) * 0.2;
                                if (el.ended) {
                                    el.src = TORRESOWO_URL + "/cat_eats_salmon.mp4";
                                    el.loop = true;
                                }
                            }
                        }}
                        loop={false}
                        src={TORRESOWO_URL + "/anuncio.mp4"}
                        className="live-stream-player__video"
                        autoPlay={autoPlay}
                        playsInline
                        onClick={e => {
                            e.target instanceof HTMLVideoElement && e.target.play();
                        }}
                        muted={isMuted}
                        controls={false}
                        style={{width: "100%", height: "100%"}}/>}
                <video
                    ref={videoRef}
                    className="live-stream-player__video"
                    autoPlay={autoPlay}
                    muted={isMuted}
                    playsInline
                    controls={false}
                    aria-label={title || "Reproductor en directo"}
                    onClick={togglePlay}
                    style={{width: "100%", height: "100%", display: firstLoad ? 'block' : 'none'}}
                >
                    <track kind="captions"/>
                </video>
                {!firstLoad && <div className="live-stream-player__loading-placeholder">
                    <p>
                        <strong>
                            Cargando stream...
                        </strong>
                    </p>
                    <span>
                        Se reproducirá en cuanto esté listo.
                    </span>
                    <Spinner/>
                </div>}
                {/* Overlay de estado */}
                {firstLoad &&
                    <div className={`live-stream-player__overlay ${status !== "playing" ? "is-visible" : ""}`}>
                        {status !== "playing" && (
                            <div className="live-stream-player__status">
                                <span className={`dot dot--${status}`}/>
                                <span>{statusLabel}</span>
                                {status === "error" && errorMsg ? (
                                    <span className="live-stream-player__error">{errorMsg}</span>
                                ) : null}
                            </div>
                        )}
                    </div>}
                {/* Controles superiores (título y badge) */}
                <div className={`live-stream-player__top-controls ${!controlsVisible ? 'is-hidden' : ''}`}>
                    <div className="live-stream-player__title-container">
                        {onClose && (
                            <button className="hlsbtn" onClick={onClose}><i
                                className="fa-solid fa-xmark"></i>
                            </button>
                        )}
                        {title ? (
                            <div className="live-stream-player__title" title={title}
                                 aria-label={`Emisión: ${title}`}>{title}</div>
                        ) : null}
                        {/* Badge LIVE */}
                        <div className={`live-stream-player__live`} aria-live="polite">
                            <span className="live-dot" aria-hidden="true"/>
                            <span>EN VIVO</span>
                        </div>
                    </div>
                </div>
                {isFullscreen && showOtherStreamsStorage.item && props.otherStreams && !isSmallMediumScreen() && <div
                    className={cn("live-stream-player__other-streams-container", controlsVisible ? 'is-visible' : '')}>
                    {props.otherStreams}
                </div>}
                {/* Controles inferiores */}
                <fieldset className={`live-stream-player__bottom-controls ${!controlsVisible ? 'is-hidden' : ''}`}
                          aria-label="Controles de reproducción">
                    {/* Play/Pause */}
                    <button className="hlsbtn" onClick={togglePlay} aria-label={isPlaying ? "Pausar" : "Reproducir"}>
                        {isPlaying ? <FaPause/> : <FaPlay/>}
                    </button>
                    {!!props.extraBtns?.length && props.extraBtns.map((btn, index) => (
                        <button className="hlsbtn" title={btn.title} onClick={btn.onClick} key={btn.title}>
                            {btn.label}
                        </button>))}
                    <div className="live-stream-player__spacer"/>
                    {/* Volumen con slider oculto - Ocultar en móviles pequeños */}
                    {!isMobile && (
                        <div className="vol-container">
                            <button className="hlsbtn" onClick={toggleMute}
                                    aria-label={isMuted ? "Activar sonido" : "Silenciar"}>
                                {getVolumeIcon()}
                            </button>
                            <div className="vol-range-container">
                                <label className="sr-only" htmlFor="vol-range">Volumen</label>
                                <input
                                    id="vol-range"
                                    className="vol-range"
                                    type="range"
                                    min="0"
                                    max="1"
                                    step="0.01"
                                    value={volume}
                                    onChange={onVolumeChange}
                                    aria-valuemin={0}
                                    aria-valuemax={1}
                                    aria-valuenow={volume}
                                    aria-label="Volumen"
                                />
                            </div>
                        </div>
                    )}
                    {/* Solo mostrar botón de mute en móviles pequeños, sin slider */}
                    {isMobile && (
                        <button className="hlsbtn" onClick={toggleMute}
                                aria-label={isMuted ? "Activar sonido" : "Silenciar"}>
                            {getVolumeIcon()}
                        </button>
                    )}
                    {isFullscreen && props.otherStreams && !isSmallMediumScreen() && (
                        <button className="hlsbtn" onClick={toggleOtherStreams}>
                            {!showOtherStreamsStorage.item && <i className="fa-regular fa-window-restore"></i>}
                            {showOtherStreamsStorage.item && <i className="fa-solid fa-window-restore"></i>}
                        </button>
                    )}
                    {/* Botón de pantalla completa */}
                    <button className="hlsbtn" onClick={toggleFullscreen}
                            aria-label={isFullscreen ? "Salir de pantalla completa" : "Pantalla completa"}>
                        {isFullscreen ? <FaCompress/> : <FaExpand/>}
                    </button>
                </fieldset>
            </div>
            {/* Indicador central de play/pause */}
            <div
                className={`live-stream-player__center-indicator ${isPlaying ? 'is-playing' : 'is-paused'} ${controlsVisible ? 'is-visible' : ''}`}>
                <div className="indicator-icon"></div>
            </div>
        </div>
    );

}

export default LiveStreamPlayer;
